import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-backed state for the native report pages.
 *
 * Every filter, matrix-expansion set, and the scroll position serialize into
 * query params so a copied/shared URL restores the EXACT report view —
 * the native replacement for the Power BI "share filters in chat" flow.
 *
 * - `useUrlBackedState(key, defaultValue, type)` — drop-in useState whose
 *   value round-trips through the URL (replaceState; no history spam).
 *   Default values are omitted from the URL to keep links clean.
 * - `useExpansionUrlState(key)` — controlled `expanded`/`onExpandedChange`
 *   pair for ReportMatrix ('~'-joined row ids in the URL).
 * - `useScrollUrlSync(ready)` — restores `sy` scroll offset once the page
 *   has data, then keeps it updated (debounced, direct history.replaceState).
 *
 * Types: 'string' | 'number' | 'bool' | 'ids' (number[]) | 'strings'
 *        | 'range' ({start,end} ISO dates) | 'json'
 */

const URL_VALUE_LIMIT = 1800; // keep links chat-friendly; longer values are dropped

function encodeValue(value, type) {
  if (value === null || value === undefined) return null;
  switch (type) {
    case 'ids':
      return Array.isArray(value) && value.length ? value.join(',') : null;
    case 'strings':
      return Array.isArray(value) && value.length ? value.map(encodeURIComponent).join('~') : null;
    case 'range':
      return value.start && value.end ? `${value.start}_${value.end}` : null;
    case 'bool':
      return value ? '1' : '0';
    case 'json': {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return null;
      }
    }
    case 'number':
      return Number.isFinite(Number(value)) ? String(value) : null;
    default:
      return String(value);
  }
}

function decodeValue(raw, type, defaultValue) {
  if (raw === null || raw === undefined || raw === '') return defaultValue;
  switch (type) {
    case 'ids':
      return raw
        .split(',')
        .map(Number)
        .filter(Number.isFinite);
    case 'strings':
      return raw.split('~').map(decodeURIComponent);
    case 'range': {
      const [start, end] = raw.split('_');
      return start && end ? { start, end } : defaultValue;
    }
    case 'bool':
      return raw === '1' || raw === 'true';
    case 'json': {
      try {
        return JSON.parse(raw);
      } catch (e) {
        return defaultValue;
      }
    }
    case 'number': {
      const n = Number(raw);
      return Number.isFinite(n) ? n : defaultValue;
    }
    default:
      return raw;
  }
}

export function useUrlBackedState(key, defaultValue, type = 'string') {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);

  const defaultRef = useRef(defaultValue);
  const value = useMemo(
    () => decodeValue(raw, type, defaultRef.current),
    [raw, type]
  );

  const set = useCallback(
    (next) => {
      // Build from the LIVE location, not the render snapshot: two setters
      // firing in one tick (e.g. setPeriod('custom') + setCustomRange(range)
      // when a date is picked) must compose. React Router's functional
      // setSearchParams hands every same-tick call the same stale snapshot,
      // so the second write would silently drop the first one's param —
      // history.replaceState is synchronous, window.location.search is truth.
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      const current = decodeValue(params.get(key), type, defaultRef.current);
      const resolved = typeof next === 'function' ? next(current) : next;
      const encoded = encodeValue(resolved, type);
      const encodedDefault = encodeValue(defaultRef.current, type);
      if (encoded === null || encoded === encodedDefault || encoded.length > URL_VALUE_LIMIT) {
        params.delete(key);
      } else {
        params.set(key, encoded);
      }
      setSearchParams(params, { replace: true });
    },
    [key, type, setSearchParams]
  );

  return [value, set];
}

/**
 * Controlled expansion state for ReportMatrix, persisted in the URL.
 * Returns [expanded (array | undefined), onExpandedChange].
 * `undefined` until the user (or URL) sets anything — the matrix then uses
 * its own defaultExpanded, so default views keep clean URLs.
 */
export function useExpansionUrlState(key = 'exp') {
  const [value, setValue] = useUrlBackedState(key, null, 'strings');
  const onExpandedChange = useCallback(
    (nextIds) => setValue(Array.isArray(nextIds) && nextIds.length ? nextIds : ['__none__']),
    [setValue]
  );
  const expanded = useMemo(() => {
    if (value === null || value === undefined) return undefined;
    return value.filter((id) => id !== '__none__');
  }, [value]);
  return [expanded, onExpandedChange];
}

/**
 * Scroll restore + sync. Pass `ready` = the page has rendered its data
 * (e.g. Boolean(data)). Restores once per URL entry, then records scroll
 * position into `sy` (debounced, replaceState directly — no router churn).
 */
export function useScrollUrlSync() {
  const restoredRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sy = Number(params.get('sy'));
    if (!Number.isFinite(sy) || sy <= 0) {
      restoredRef.current = true;
      return undefined;
    }
    // Poll until the page has rendered enough height (data/charts painted),
    // then restore the shared scroll position. Gives up after ~8s.
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const reachable = document.documentElement.scrollHeight >= sy + window.innerHeight * 0.4;
      if (reachable || attempts >= 32) {
        clearInterval(timer);
        window.scrollTo({ top: sy, behavior: 'auto' });
        restoredRef.current = true;
      }
    }, 250);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let timer = null;
    const onScroll = () => {
      if (!restoredRef.current) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        const y = Math.round(window.scrollY);
        if (y > 0) params.set('sy', String(y));
        else params.delete('sy');
        const qs = params.toString();
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
        );
      }, 350);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
}

/** Current share URL (always reflects live state thanks to the hooks above). */
export function currentReportUrl() {
  return window.location.href;
}
